import { useEffect, useState } from "react";
import {
  Box, Paper, Button, Alert, TextField, InputAdornment, Stack, Typography,
  Dialog, DialogContent, DialogActions, CircularProgress,
  FormControlLabel, Switch, Grid2 as Grid, MenuItem, Tooltip,
} from "@mui/material";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { StatusPill } from "@/components/StatusPill";
import { ItemPicker } from "@/components/Pickers";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  itemSubstitutesApi, type ItemSubstituteDto, type ItemDto, type SubstituteReasonCode,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const REASONS: { value: SubstituteReasonCode; label: string; hint: string }[] = [
  { value: "SameApi", label: "Same API", hint: "Identical active ingredient and strength" },
  { value: "Generic", label: "Generic", hint: "Generic equivalent of a brand" },
  { value: "TherapeuticEquiv", label: "Therapeutic equivalent", hint: "Different molecule, same clinical effect" },
  { value: "Formulary", label: "Formulary", hint: "Preferred by the site's formulary" },
];
const reasonLabel = (r: SubstituteReasonCode) => REASONS.find(x => x.value === r)?.label ?? r;

/**
 * Item Substitute master — the approved "dispense B instead of A" pairs.
 *
 * This mapping is the only clinical control the app has over substitution: billing refuses a swap
 * that is not an approved pair, and nothing verifies that two mapped drugs are actually
 * equivalent. So the screen leads with the one signal it can compute — whether both items resolve
 * to the same generic — and makes crossing generics a deliberate, acknowledged act.
 */
export function ItemSubstitutesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [crossGenericOnly, setCrossGenericOnly] = useState(false);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ItemSubstituteDto | null>(null);

  const canManage = useAuth(s => s.hasPermission("sales.substitution.manage"));

  const query = useQuery({
    queryKey: ["item-substitutes", search, crossGenericOnly, pagination.page, pagination.pageSize],
    queryFn: () => itemSubstitutesApi.list({
      search: search || undefined,
      crossGenericOnly: crossGenericOnly || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    meta: { successMessage: "Substitution pair removed" },
    mutationFn: (id: string) => itemSubstitutesApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["item-substitutes"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (row: ItemSubstituteDto) => { setEditing(row); setFormOpen(true); };
  const handleDelete = (row: ItemSubstituteDto) => {
    if (confirm(
      `Stop offering "${row.substituteItemName}" as a substitute for "${row.primaryItemName}"?\n\n`
      + "Invoices already billed under this pair are unaffected.")) {
      deleteMutation.mutate(row.id);
    }
  };

  const rows = query.data?.items ?? [];
  const crossGenericCount = rows.filter(r => !r.sameGeneric && r.isActive).length;

  const columns: GridColDef<ItemSubstituteDto>[] = [
    {
      field: "primaryItemName", headerName: "Prescribed", flex: 1.3, minWidth: 200,
      renderCell: (p) => (
        <Stack sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {p.row.primaryItemName}
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.primaryItemSku}{p.row.primaryGenericName ? ` · ${p.row.primaryGenericName}` : ""}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "arrow", headerName: "", width: 40, sortable: false, filterable: false, align: "center",
      renderCell: () => <ArrowRightAltIcon fontSize="small" color="disabled" />,
    },
    {
      field: "substituteItemName", headerName: "Dispense instead", flex: 1.3, minWidth: 200,
      renderCell: (p) => (
        <Stack sx={{ minWidth: 0 }}>
          <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {p.row.substituteItemName}
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {p.row.substituteItemSku}{p.row.substituteGenericName ? ` · ${p.row.substituteGenericName}` : ""}
          </Typography>
        </Stack>
      ),
    },
    {
      field: "sameGeneric", headerName: "Generic", width: 140, sortable: false,
      renderCell: (p) => p.row.sameGeneric
        ? <StatusPill label="Same generic" tone="success" />
        : (
          <Tooltip title="The two items do not resolve to the same generic master row. Verify this pair is clinically equivalent.">
            <span><StatusPill label="Cross-generic" tone="warning" /></span>
          </Tooltip>
        ),
    },
    { field: "priority", headerName: "Priority", width: 90, align: "right", headerAlign: "right" },
    {
      field: "reasonCode", headerName: "Reason", width: 170,
      valueGetter: (_v, row) => reasonLabel(row.reasonCode),
    },
    {
      field: "requiresPharmacistConfirm", headerName: "Confirm", width: 120, sortable: false,
      renderCell: (p) => p.row.requiresPharmacistConfirm
        ? <StatusPill label="Required" tone="info" />
        : <Box component="span" sx={{ color: "text.disabled" }}>Not required</Box>,
    },
    {
      field: "isActive", headerName: "Status", width: 120, sortable: false,
      renderCell: (p) => p.row.substituteInactive
        ? (
          <Tooltip title="The substitute item is deactivated, so this pair is never offered.">
            <span><StatusPill label="Item retired" tone="critical" /></span>
          </Tooltip>
        )
        : <StatusPill label={p.row.isActive ? "Active" : "Inactive"} />,
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canManage, onClick: () => openEdit(p.row) },
          {
            label: "Remove", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canManage,
            onClick: () => handleDelete(p.row),
          },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Catalog"]}
        title="Item Substitutes"
        subtitle="Approved pairs for dispensing one item in place of another. Billing refuses any substitution that is not mapped here."
        actions={canManage ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New pair</Button>
        ) : undefined}
      />

      {crossGenericCount > 0 && !crossGenericOnly && (
        <Alert
          severity="warning" sx={{ mb: 2 }}
          action={<Button size="small" onClick={() => setCrossGenericOnly(true)}>Review</Button>}
        >
          {crossGenericCount} active pair(s) on this page map across different generics. Nothing
          checks that those drugs are equivalent — only the pharmacist's confirmation stands behind them.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            placeholder="Search by item name or SKU"
            fullWidth size="small" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <FormControlLabel
            sx={{ flexShrink: 0 }}
            control={<Switch checked={crossGenericOnly} onChange={(_, v) => setCrossGenericOnly(v)} />}
            label="Cross-generic only"
          />
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            getRowHeight={() => 56}
            onRowDoubleClick={(p) => canManage && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <PairDialog open={formOpen} onClose={() => setFormOpen(false)} row={editing} />
    </Box>
  );
}

function PairDialog({
  open, onClose, row,
}: { open: boolean; onClose: () => void; row: ItemSubstituteDto | null }) {
  const qc = useQueryClient();
  const isEdit = !!row;

  const [primary, setPrimary] = useState<ItemDto | null>(null);
  const [substitute, setSubstitute] = useState<ItemDto | null>(null);
  const [priority, setPriority] = useState("1");
  const [reasonCode, setReasonCode] = useState<SubstituteReasonCode>("SameApi");
  const [requiresConfirm, setRequiresConfirm] = useState(true);
  const [alsoReverse, setAlsoReverse] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [crossGenericAck, setCrossGenericAck] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPrimary(null);
    setSubstitute(null);
    setPriority(row ? String(row.priority) : "1");
    setReasonCode(row?.reasonCode ?? "SameApi");
    setRequiresConfirm(row?.requiresPharmacistConfirm ?? true);
    setAlsoReverse(!row);
    setIsActive(row?.isActive ?? true);
    setCrossGenericAck(false);
  }, [open, row]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Pair updated" : "Pair mapped" },
    mutationFn: async () => {
      if (isEdit && row) {
        await itemSubstitutesApi.update(row.id, {
          priority: Number(priority || 1), reasonCode,
          requiresPharmacistConfirm: requiresConfirm, isActive,
        });
        return;
      }
      await itemSubstitutesApi.create({
        primaryItemId: primary!.id, substituteItemId: substitute!.id,
        priority: Number(priority || 1), reasonCode,
        requiresPharmacistConfirm: requiresConfirm, alsoReverse,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["item-substitutes"] });
      onClose();
    },
  });

  // Only computable when both items are picked and both carry a linked generic. Two unlinked
  // items are "unknown", not "same" — an absent link tells us nothing about equivalence.
  const bothPicked = !!primary && !!substitute;
  const sameGeneric = bothPicked
    && !!primary!.genericNameId
    && primary!.genericNameId === substitute!.genericNameId;
  const crossGeneric = bothPicked && !sameGeneric;

  const err =
    isEdit ? (Number(priority) > 0 ? null : "Priority must be 1 or more")
    : !primary ? "Pick the prescribed item"
    : !substitute ? "Pick the item to dispense instead"
    : primary.id === substitute.id ? "An item cannot substitute itself"
    : Number(priority) <= 0 ? "Priority must be 1 or more"
    : crossGeneric && !crossGenericAck ? "Confirm the cross-generic mapping below"
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => { e.preventDefault(); if (!err) mutation.mutate(); }}>
        <ClosableDialogTitle onClose={onClose}>
          {isEdit ? "Edit substitution pair" : "New substitution pair"}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

          {isEdit && row ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{row.primaryItemName}</strong> → <strong>{row.substituteItemName}</strong>
              <Typography variant="caption" display="block">
                The pair itself cannot be re-pointed — remove it and map a new one instead.
              </Typography>
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid size={12}>
                <ItemPicker label="Prescribed item" value={primary} onChange={setPrimary} required
                  helperText="The item on the prescription that may be out of stock" />
              </Grid>
              <Grid size={12}>
                <ItemPicker label="Dispense instead" value={substitute} onChange={setSubstitute} required
                  helperText={primary?.genericName
                    ? `Prefer an item with the same generic — ${primary.genericName}`
                    : "Prefer an item with the same generic"} />
              </Grid>
            </Grid>
          )}

          {bothPicked && !isEdit && (
            sameGeneric ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Both items resolve to the same generic. This is the safe case.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  These items do <strong>not</strong> resolve to the same generic
                  {primary?.genericName || substitute?.genericName
                    ? ` (${primary?.genericName ?? "unlinked"} vs ${substitute?.genericName ?? "unlinked"})`
                    : ""}.
                  Nothing in the app checks that they are clinically equivalent, and the dispensed
                  quantity is not adjusted for strength.
                </Typography>
                <FormControlLabel
                  control={<Switch checked={crossGenericAck} onChange={(_, v) => setCrossGenericAck(v)} />}
                  label="I have verified these are interchangeable"
                />
              </Alert>
            )
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Priority" type="number" size="small" fullWidth
                value={priority} onChange={(e) => setPriority(e.target.value)}
                inputProps={{ min: 1 }} helperText="1 is offered first" />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField select label="Reason" size="small" fullWidth
                value={reasonCode} onChange={(e) => setReasonCode(e.target.value as SubstituteReasonCode)}
                helperText={REASONS.find(r => r.value === reasonCode)?.hint}>
                {REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={<Switch checked={requiresConfirm} onChange={(_, v) => setRequiresConfirm(v)} />}
                label="Require pharmacist confirmation at billing"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {requiresConfirm
                  ? "Billing rejects this substitution unless a pharmacist accepts it, and records who did."
                  : "The swap can be billed without anyone accepting responsibility for it."}
              </Typography>
            </Grid>
            {!isEdit && (
              <Grid size={12}>
                <FormControlLabel
                  control={<Switch checked={alsoReverse} onChange={(_, v) => setAlsoReverse(v)} />}
                  label="Also map the reverse pair"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Pairs are one-way. Without this, the substitute will not be offered for the
                  prescribed item's own shortage.
                </Typography>
              </Grid>
            )}
            {isEdit && (
              <Grid size={12}>
                <FormControlLabel
                  control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />}
                  label="Active"
                />
              </Grid>
            )}
          </Grid>

          {err && <Alert severity="info" sx={{ mt: 2 }}>{err}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!!err || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save" : "Map pair"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
